import { z } from 'zod'

import { nonEmptyArraySchema } from '@mntn-dev/utilities'
import { EnumBuilder, type ZodInfer } from '@mntn-dev/utility-types'

import { ModelFilterSchema } from '../model-filter-schema.ts'
import { NonEmptyStringSchema } from './property.models.ts'
import {
  DeliverableIdSchema,
  FileIdSchema,
  ProjectIdSchema,
  ProjectServiceIdSchema,
} from './unique-id.models.ts'

/**
 * DeliverableCategory
 */
export const [
  DeliverableCategories,
  DeliverableCategorySchema,
  DeliverableCategoryEnum,
] = EnumBuilder('video', 'file', 'archive')
export type DeliverableCategory = ZodInfer<typeof DeliverableCategorySchema>

/**
 * VideoAspectRatio
 */
export const [VideoAspectRatios, VideoAspectRatioSchema, VideoAspectRatioEnum] =
  EnumBuilder('1:1', '2:3', '4:5', '9:16', '16:9', 'other')
export type VideoAspectRatio = ZodInfer<typeof VideoAspectRatioSchema>

/**
 * VideoCutType
 */
export const [VideoCutTypes, VideoCutTypeSchema, VideoCutTypeEnum] =
  EnumBuilder('hero-cut', 'concept', 'cut-down', 'variation')
export type VideoCutType = ZodInfer<typeof VideoCutTypeSchema>

/**
 * VideoDuration
 * TODO: Consider using a more specific type for duration, like: z.union([z.literal(15), z.literal(30)])
 */
export const VideoDurationFixedSchema = z.int().nonnegative()
export type VideoDurationFixed = ZodInfer<typeof VideoDurationFixedSchema>

export const VideoDurationRangeSchema = z
  .object({
    min: VideoDurationFixedSchema,
    max: VideoDurationFixedSchema,
  })
  .refine(({ min, max }) => min <= max, 'min must be less than or equal to max')
export type VideoDurationRange = ZodInfer<typeof VideoDurationRangeSchema>

export const VideoDurationOptionsSchema = z.union([
  VideoDurationFixedSchema,
  VideoDurationRangeSchema,
])
export type VideoDurationOptions = ZodInfer<typeof VideoDurationOptionsSchema>

export const ArrayOfVideoDurationOptionsSchema = nonEmptyArraySchema(
  VideoDurationOptionsSchema
)
export type ArrayOfVideoDurationOptions = ZodInfer<
  typeof ArrayOfVideoDurationOptionsSchema
>

export const VideoDurationSchema = z.union([
  VideoDurationOptionsSchema,
  ArrayOfVideoDurationOptionsSchema,
])
export type VideoDuration = ZodInfer<typeof VideoDurationSchema>

export function isVideoDurationRange(
  duration: VideoDuration
): duration is VideoDurationRange {
  return VideoDurationRangeSchema.safeParse(duration).success
}

export function isVideoDurationFixed(
  duration: VideoDuration
): duration is VideoDurationFixed {
  return VideoDurationFixedSchema.safeParse(duration).success
}

export function isVideoDurationArray(
  duration: VideoDuration
): duration is ArrayOfVideoDurationOptions {
  return ArrayOfVideoDurationOptionsSchema.safeParse(duration).success
}

/**
 * ChannelId
 */
export const [ChannelIds, ChannelIdSchema, ChannelIdEnum] = EnumBuilder(
  'facebook',
  'tiktok',
  'instagram',
  'ctv',
  'social',
  'linkedin',
  'other'
)
export type ChannelId = ZodInfer<typeof ChannelIdSchema>

/**
 * DeliverableReviewLevel
 */
export const [
  DeliverableReviewLevels,
  DeliverableReviewLevelSchema,
  DeliverableReviewLevelEnum,
] = EnumBuilder('none', 'primary', 'secondary')
export type DeliverableReviewLevel = ZodInfer<
  typeof DeliverableReviewLevelSchema
>

export const DeliverableNameSchema = NonEmptyStringSchema()

const DeliverableDetailsBaseSchema = z.object({
  name: DeliverableNameSchema.optional(),
  reviewLevel: DeliverableReviewLevelSchema,
})

export const VideoDeliverableDetailsSchema =
  DeliverableDetailsBaseSchema.extend({
    category: DeliverableCategorySchema.extract(['video']),
    cut: VideoCutTypeSchema,
    duration: VideoDurationSchema,
    channel: ChannelIdSchema.optional(),
    aspectRatio: VideoAspectRatioSchema.optional(),
  })

export type VideoDeliverableDetails = ZodInfer<
  typeof VideoDeliverableDetailsSchema
>

export function isVideoDeliverable(
  deliverable: DeliverableDetails
): deliverable is VideoDeliverableDetails {
  return VideoDeliverableDetailsSchema.safeParse(deliverable).success
}

export const FileDeliverableDetailsSchema = DeliverableDetailsBaseSchema.extend(
  {
    category: DeliverableCategorySchema.extract(['file']),
  }
)

export type FileDeliverableDetails = ZodInfer<
  typeof FileDeliverableDetailsSchema
>

export const ArchiveDeliverableDetailsSchema =
  DeliverableDetailsBaseSchema.extend({
    category: DeliverableCategorySchema.extract(['archive']),
  })

export type ArchiveDeliverableDetails = ZodInfer<
  typeof ArchiveDeliverableDetailsSchema
>

export const DeliverableDetailsSchema = z.discriminatedUnion('category', [
  VideoDeliverableDetailsSchema,
  FileDeliverableDetailsSchema,
  ArchiveDeliverableDetailsSchema,
])

export type DeliverableDetails = ZodInfer<typeof DeliverableDetailsSchema>

/**
 * DeliverableDomainSelectModel
 */
export const DeliverableDomainSelectModelSchema = z.object({
  deliverableId: DeliverableIdSchema,
  projectId: ProjectIdSchema,
  projectServiceId: ProjectServiceIdSchema,
  details: DeliverableDetailsSchema,
  fileId: FileIdSchema.optional(),
})

export type DeliverableDomainSelectModel = ZodInfer<
  typeof DeliverableDomainSelectModelSchema
>

/**
 * DeliverableDomainInsertModel
 */
export const DeliverableDomainInsertModelSchema =
  DeliverableDomainSelectModelSchema.omit({ fileId: true }).partial({
    deliverableId: true,
  })

export type DeliverableDomainInsertModel = ZodInfer<
  typeof DeliverableDomainInsertModelSchema
>

/**
 * DeliverableDomainUpdateModel
 */
export const DeliverableDomainUpdateModelSchema =
  DeliverableDomainSelectModelSchema.pick({
    fileId: true,
  }).partial()

export type DeliverableDomainUpdateModel = ZodInfer<
  typeof DeliverableDomainUpdateModelSchema
>

/**
 * DeliverableDomainSelectModelFilter
 */
export const DeliverableDomainSelectModelFiltersSchema = z
  .object({
    deliverableId: ModelFilterSchema(DeliverableIdSchema),
    projectId: ModelFilterSchema(ProjectIdSchema),
    projectServiceId: ModelFilterSchema(ProjectServiceIdSchema),
    fileId: ModelFilterSchema(FileIdSchema),
  })
  .partial()

export type DeliverableDomainSelectModelFilters = ZodInfer<
  typeof DeliverableDomainSelectModelFiltersSchema
>
